import { observable, action, computed } from 'mobx'
import { browserHistory } from 'react-router'
import moment from 'moment'
import { Location } from 'history'
import { LocalStorage } from '../../../../utilities/LocalStorage'
import { base64Decode } from '../../../../utilities/encryption'

export enum ReportTimeframe {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  WEEK = 'week',
}

export enum GroupByOptions {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
}

export enum GroupByObject {
  PRODUCT = 'product',
  CATEGORY = 'category',
}

export enum FixedDatesOptions {
  '7DAY' = '7 days',
  '14DAY' = '14 days',
  '30DAY' = '30 days',
}
export class ReportFilterStore {
  @observable
  companyId: number

  @observable
  location: Location

  @observable
  startDate: Date // YYYY-MM-DD format

  @observable
  endDate: Date // YYYY-MM-DD format

  @observable
  selectedTimeframe: ReportTimeframe = ReportTimeframe.TODAY

  @observable
  groupBy: GroupByOptions | GroupByObject

  @observable
  fixedDates: FixedDatesOptions

  @observable
  parentCategoryIds: string[] = []

  @observable
  childCategoryIds: string[] = []

  @observable
  supplierIds: number[] = []

  @computed
  get startDateString(): string {
    return moment(this.startDate).format('YYYY-MM-DD')
  }

  @computed
  get endDateString(): string {
    return moment(this.endDate).format('YYYY-MM-DD')
  }

  get query(): Record<string, string | string[]> {
    return browserHistory.getCurrentLocation().query
  }

  // Locations the current user has access to
  @observable
  permittedLocationIds: number[]

  // Currently selected locations out of the permitted locations
  @observable
  selectedLocationIds: number[]

  @observable
  includeDeletedProducts = true

  @observable
  hideEmptyProduct = true

  // Field to force re-renders
  @observable
  isLoading = false

  @observable
  isFirstLoad = true

  constructor(
    companyId: number,
    location: Location,
    permittedLocationIds: number[],
    isAdvancedReport = false,
  ) {
    this.companyId = companyId

    this.location = location

    // The locations the user has permissions to access to is passed in
    this.permittedLocationIds = permittedLocationIds

    // Fill in start date, end date, and location IDs via the url
    const query = location.query
    this.groupBy = query.groupBy || GroupByOptions.DAY
    this.fixedDates = query.fixedDates || FixedDatesOptions['7DAY']

    try {
      this.parentCategoryIds = query.parentCategoryIds
        ? JSON.parse(base64Decode(query.parentCategoryIds))
        : []
      this.childCategoryIds = query.childCategoryIds
        ? JSON.parse(base64Decode(query.childCategoryIds))
        : []
    } catch (err) {
      this.parentCategoryIds = []
      this.childCategoryIds = []
    }

    this.startDate = this.getQueryStartDate(query, isAdvancedReport)
    this.endDate = this.getQueryEndDate(query, isAdvancedReport)

    this.selectedLocationIds = this.getCleanedLocationIds(
      query,
      permittedLocationIds,
      isAdvancedReport,
    )

    this.groupBy = query.groupByCategoryOrProduct || query.GroupByOptions
    this.includeDeletedProducts = query.includeDeletedProducts === 'true'
    this.hideEmptyProduct = query.hideEmptyProduct === 'true'

    // If in regular report and startDate and endDate exist in query, then apply
    if (!isAdvancedReport && query.startDate && query.endDate) {
      this.apply()
    }
  }

  getQueryStartDate = (query: any, isAdvancedReport: boolean) => {
    if (query.startDate) {
      return moment(query.startDate).startOf('day').toDate()
    }

    if (isAdvancedReport) {
      return moment().startOf('month').toDate()
    }

    return moment().startOf('day').toDate()
  }

  getQueryEndDate = (query: any, isAdvancedReport: boolean) => {
    if (query.endDate) {
      return moment(query.endDate).endOf('day').toDate()
    }

    if (isAdvancedReport) {
      return moment().endOf('month').toDate()
    }

    return moment().endOf('day').toDate()
  }

  getCleanedLocationIds = (
    query: any,
    permittedLocationIds: number[],
    isAdvancedReport: boolean,
  ) => {
    let selectedLocationIds: number[] = []

    if (Array.isArray(query.locationIds)) {
      selectedLocationIds = query.locationIds
        ? query.locationIds.map((id: string) => parseInt(id))
        : permittedLocationIds
    } else if (query.locationIds) {
      selectedLocationIds = [parseInt(query.locationIds)]
    } else {
      selectedLocationIds = [permittedLocationIds[0]]
    }

    if (Object.keys(query).length === 0) {
      if (isAdvancedReport) {
        selectedLocationIds = permittedLocationIds
      } else {
        const queryObject = LocalStorage.getReportQueryFilter()
        selectedLocationIds =
          queryObject[this.companyId]?.locationIds.length > 0
            ? queryObject[this.companyId].locationIds
            : [permittedLocationIds[0]]
      }
    }
    // If someone maliciously injects a location ID they do not have permissions for, clear out the location ID
    return selectedLocationIds.filter((id) => permittedLocationIds.includes(id))
  }

  @action
  setIsFirstLoad(isFirstLoad: boolean) {
    this.isFirstLoad = isFirstLoad
  }

  @action
  setGroupBy(groupBy: GroupByOptions) {
    this.groupBy = groupBy
    if (groupBy === GroupByOptions.WEEK) {
      this.startDate = moment(this.startDate).startOf('week').toDate()
      this.endDate = moment(this.endDate).endOf('week').toDate()
    }
  }
  @action
  setFixedDates = (fixedDates: FixedDatesOptions) => {
    this.fixedDates = fixedDates
  }
  @action
  setStartDate(date: Date | null, selectedTimeframe?: ReportTimeframe) {
    this.startDate = date || moment().startOf('day').toDate()
    this.setSelectedTimeframe(selectedTimeframe || ReportTimeframe.TODAY)
  }

  @action
  setEndDate(date: Date | null, selectedTimeframe?: ReportTimeframe) {
    this.endDate = date || moment().endOf('day').toDate()
    this.setSelectedTimeframe(selectedTimeframe || ReportTimeframe.TODAY)
  }

  @action
  setSelectedLocationIds(locationIds: number[]) {
    this.selectedLocationIds = locationIds
  }

  @action
  setSelectedTimeframe = (selectedTimeframe: ReportTimeframe) => {
    this.selectedTimeframe = selectedTimeframe
  }

  @action
  getFiltersChanged = (localFilters: Record<string, unknown | unknown[]>) =>
    Object.entries(localFilters).filter(
      ([filterKey, filterVal]) => `${filterVal}` !== `${this.query[filterKey]}`,
    )

  @action
  saveToQuery(query?: Record<string, unknown>) {
    this.replaceInvalidDate(this.startDate, this.endDate)

    const { startDate, endDate } = this
    if (startDate > endDate) {
      this.startDate = endDate
      this.endDate = startDate
    }

    const queryObject = query || {
      groupBy: this.groupBy,
      startDate: this.startDateString,
      endDate: this.endDateString,
      locationIds: this.selectedLocationIds,
    }

    browserHistory.replace({
      pathname: browserHistory.getCurrentLocation().pathname,
      query: queryObject,
    })

    const localStorageFilter = LocalStorage.getReportQueryFilter()
    localStorageFilter[this.companyId] = {
      locationIds: queryObject.locationIds,
    }
    LocalStorage.setReportQueryFilter(localStorageFilter)
  }

  @action
  apply() {
    this.isLoading = true
    const queryObject = {
      startDate: this.startDateString,
      endDate: this.endDateString,
      locationIds: this.selectedLocationIds,
      groupByCategoryOrProduct: this.groupBy,
      includeDeletedProducts: this.includeDeletedProducts,
      hideEmptyProduct: this.hideEmptyProduct,
    }
    this.saveToQuery(queryObject)

    setTimeout(() => (this.isLoading = false), 100)
  }
  @action
  setSelectedParentCategoryIds = (parentCategoryIds: string[]) => {
    this.parentCategoryIds = parentCategoryIds
  }
  @action
  setSelectedChildCategoryIds = (childCategoryIds: string[]) => {
    this.childCategoryIds = childCategoryIds
  }
  @action
  setSelectedSupplierIds = (supplierIds: number[]) => {
    this.supplierIds = supplierIds
  }
  @action
  reset() {
    this.parentCategoryIds = []
    this.supplierIds = []
    this.childCategoryIds = []
  }
  getGroupBy() {
    return this.groupBy
  }

  get isLongerThanOneDay(): boolean {
    return this.startDateString !== this.endDateString
  }

  replaceInvalidDate(startDate: Date, endDate: Date) {
    const isValidStartDate = this.validateDate(startDate)
    const isValidEndDate = this.validateDate(endDate)
    if (!isValidStartDate) {
      isValidEndDate
        ? this.setStartDate(endDate)
        : this.setStartDate(moment().startOf('day').toDate())
    }
    if (!isValidEndDate) {
      isValidStartDate
        ? this.setEndDate(startDate)
        : this.setEndDate(moment().endOf('day').toDate())
    }
  }

  validateDate(date: Date) {
    return !isNaN(Date.parse(date.toString()))
  }
}
